// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Sparkles,
  Star,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { InlineDeleteConfirm } from '@/app/components/inline-delete-confirm';
import { PageHeader, EntryCard, FormCard, FieldHeader } from '@/app/components/page-layout';
import { BulkActionBar, SelectionCheckbox } from '@/app/components/bulk-action-bar';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import type { BulkAction } from '@/app/components/bulk-action-bar';
import { useBulkSelection } from '@/app/hooks/useBulkSelection';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import {
  useAccomplishments,
  useCreateAccomplishment,
  useUpdateAccomplishment,
  useDeleteAccomplishment,
  useBulkDeleteAccomplishments,
  useSuggestAccomplishments,
  useBulkCreateAccomplishments,
  useSuggestAccomplishmentTags,
  usePolishAccomplishment,
  accomplishmentSchema,
} from '@/app/features/accomplishment';
import type { AccomplishmentFormData } from '@/app/features/accomplishment';

const CATEGORIES = ['technical', 'leadership', 'process', 'revenue'] as const;

type Accomplishment = {
  id: string;
  experience_id?: string;
  description: string;
  impact?: string;
  category?: string;
  tags?: string[];
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type Suggestion = {
  description: string;
  impact?: string;
  category?: string;
  suggested_tags?: string[];
};

export function AccomplishmentsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const accomplishments = useAccomplishments({
    search: searchTerm || undefined,
    category: categoryFilter || undefined,
  });
  const createAccomplishment = useCreateAccomplishment();
  const updateAccomplishment = useUpdateAccomplishment();
  const deleteAccomplishment = useDeleteAccomplishment();
  const bulkDeleteAccomplishments = useBulkDeleteAccomplishments();
  const suggestAccomplishments = useSuggestAccomplishments();
  const bulkCreateAccomplishments = useBulkCreateAccomplishments();
  const suggestTags = useSuggestAccomplishmentTags();
  const polishAccomplishment = usePolishAccomplishment();


  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AccomplishmentFormData>({
    experience_id: '',
    description: '',
    impact: '',
    category: '',
    tags: '',
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof AccomplishmentFormData, string>>
  >({});
  const [formPolishing, setFormPolishing] = useState(false);
  const [formPolishResult, setFormPolishResult] = useState<string | null>(null);

  // Per-accomplishment polish state: maps ID -> polished text (null = loading)
  const [polishedTexts, setPolishedTexts] = useState<
    Record<string, string | null>
  >({});
  // Per-accomplishment polish errors: maps ID -> error message
  const [polishErrors, setPolishErrors] = useState<Record<string, string>>({});

  // Suggestions state
  const [showSuggestPanel, setShowSuggestPanel] = useState(false);
  const [suggestJobTitle, setSuggestJobTitle] = useState('');
  const [includeJobDescriptions, setIncludeJobDescriptions] = useState(true);
  const [includeRoleHighlights, setIncludeRoleHighlights] = useState(true);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(
    new Set(),
  );
  const suggestPanelRef = useRef<HTMLDivElement>(null);

  const unsaved = useUnsavedChanges(formData, { enabled: !!editingId });
  const editFormRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setFormData({
      experience_id: '',
      description: '',
      impact: '',
      category: '',
      tags: '',
    });
    setFieldErrors({});
    setFormPolishing(false);
    setFormPolishResult(null);
    setEditingId(null);
    setIsFormOpen(false);
    unsaved.clear();
  }, [unsaved]);

  function openCreateForm() {
    resetForm();
    setIsFormOpen(true);
  }

  function openEditForm(accomplishment: Accomplishment) {
    unsaved.guardClose(() => {
      const data: AccomplishmentFormData = {
        experience_id: accomplishment.experience_id ?? '',
        description: accomplishment.description,
        impact: accomplishment.impact ?? '',
        category: accomplishment.category ?? '',
        tags: accomplishment.tags?.join(', ') ?? '',
      };
      setFormData(data);
      unsaved.snapshot(data);
      setFieldErrors({});
      setEditingId(accomplishment.id);
      setIsFormOpen(true);
      // Scroll the inline form into view and focus its first input.
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        editFormRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
      }, 50);
    });
  }

  function handleCloseForm() {
    unsaved.guardClose(resetForm);
  }

  function handleChange(field: keyof AccomplishmentFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = accomplishmentSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof AccomplishmentFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof AccomplishmentFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const onSuccess = () => {
      resetForm();
      toast.success('Accomplishment saved successfully.');
    };

    if (editingId) {
      updateAccomplishment.mutate(
        { ...result.data, id: editingId },
        { onSuccess },
      );
    } else {
      createAccomplishment.mutate(result.data, { onSuccess });
    }
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteAccomplishment.mutate(id, {
      onSuccess: () => {
        setDeletingId(null);
        if (editingId === id) resetForm();
      },
      onError: () => setDeletingId(null),
    });
  }

  // --- Polish ---

  function handlePolish(id: string) {
    setPolishedTexts((prev) => ({ ...prev, [id]: null }));
    setPolishErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
    polishAccomplishment.mutate(
      { id },
      {
        onSuccess: (result) => {
          setPolishedTexts((prev) => ({
            ...prev,
            [id]: (result as { polished: string }).polished,
          }));
        },
        onError: (err) => {
          setPolishedTexts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          const msg = err instanceof Error ? err.message : 'Polish failed';
          setPolishErrors((prev) => ({ ...prev, [id]: msg }));
          setTimeout(() => setPolishErrors((prev) => { const next = { ...prev }; delete next[id]; return next; }), 8000);
        },
      },
    );
  }

  function handleAcceptPolish(id: string) {
    const polished = polishedTexts[id];
    if (!polished) return;
    const accomplishmentList = (accomplishments.data ?? []) as Accomplishment[];
    const acc = accomplishmentList.find((a) => a.id === id);
    if (!acc) return;
    // Replace the description directly with the AI result.
    updateAccomplishment.mutate(
      {
        id,
        description: polished,
        impact: acc.impact ?? '',
        category: acc.category ?? '',
        experience_id: acc.experience_id ?? '',
        tags: (acc.tags ?? []).join(', '),
      },
      {
        onSuccess: () => {
          setPolishedTexts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
      },
    );
  }

  function dismissPolish(id: string) {
    setPolishedTexts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  // --- Suggestions ---

  function openSuggestPanel() {
    setSuggestJobTitle('');
    setIncludeJobDescriptions(true);
    setIncludeRoleHighlights(true);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setShowSuggestPanel(true);
  }

  function closeSuggestPanel() {
    setShowSuggestPanel(false);
    setSuggestJobTitle('');
    setIncludeJobDescriptions(true);
    setIncludeRoleHighlights(true);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
  }

  function handleGenerateSuggestions(e: React.FormEvent) {
    e.preventDefault();
    if (
      !suggestJobTitle.trim() &&
      !includeJobDescriptions &&
      !includeRoleHighlights
    ) {
      return;
    }

    suggestAccomplishments.mutate({
      job_title: suggestJobTitle.trim() || undefined,
      include_job_descriptions: includeJobDescriptions,
      include_role_highlights: includeRoleHighlights,
    }, {
      onSuccess: (data) => {
        setSuggestions(data);
        // Start with none selected; user must explicitly choose.
        setSelectedSuggestions(new Set());
        setTimeout(() => {
          suggestPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 50);
      },
    });
  }

  function toggleSuggestion(index: number) {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  function handleAcceptSuggestions() {
    const selected = suggestions
      .filter((_, i) => selectedSuggestions.has(i))
      .map((suggestion) => ({
        description: suggestion.description,
        impact: suggestion.impact || undefined,
        category: suggestion.category || undefined,
        tags: suggestion.suggested_tags?.length
          ? suggestion.suggested_tags
          : undefined,
      }));

    if (selected.length === 0) return;

    bulkCreateAccomplishments.mutate(selected, {
      onSuccess: () => {
        closeSuggestPanel();
        toast.success(
          `${selected.length} accomplishment${selected.length !== 1 ? 's' : ''} added successfully.`,
        );
      },
    });
  }

  const isPending =
    createAccomplishment.isPending || updateAccomplishment.isPending;

  const accomplishmentList = (accomplishments.data ??
    []) as Accomplishment[];

  const allAccIds = useMemo(() => accomplishmentList.map((a) => a.id), [accomplishmentList]);
  const bulk = useBulkSelection(allAccIds);
  const inSelectionMode = !bulk.noneSelected;

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onExecute: async (ids) => {
          await bulkDeleteAccomplishments.mutateAsync(ids);
        },
      },
    ],
    [bulkDeleteAccomplishments],
  );

  const accomplishmentForm = (
    <FormCard className={editingId ? 'mt-0 sm:mt-0' : undefined}>
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-foreground">
          {editingId ? 'Edit Accomplishment' : 'New Accomplishment'}
        </h2>
        <button
          type="button"
          onClick={handleCloseForm}
          className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close form"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(createAccomplishment.error ?? updateAccomplishment.error) && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {(createAccomplishment.error ?? updateAccomplishment.error)?.message}
        </div>
      )}

      <form
        className="mt-4 space-y-4"
        onSubmit={handleSubmit}
        noValidate
      >
        <div>
          <FieldHeader htmlFor="acc-description" label="Description">
            <button
              type="button"
              disabled={
                !formData.description.trim() ||
                polishAccomplishment.isPending ||
                formPolishing
              }
              onClick={() => {
                setFormPolishing(true);
                setFormPolishResult(null);
                const params: {
                  id?: string;
                  description?: string;
                  impact?: string;
                  category?: string;
                } = editingId
                  ? { id: editingId }
                  : {
                      description: formData.description,
                      impact: formData.impact || undefined,
                      category: formData.category || undefined,
                    };
                polishAccomplishment.mutate(params, {
                  onSuccess: (result) => {
                    setFormPolishResult(
                      (result as { polished: string }).polished,
                    );
                    setFormPolishing(false);
                  },
                  onError: () => {
                    setFormPolishing(false);
                  },
                });
              }}
              className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
              title="AI polish this description ($0.01)"
            >
              {formPolishing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              AI Polish
            </button>
          </FieldHeader>
          <textarea
            id="acc-description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Built CI/CD pipeline reducing deploy time by 40%"
            aria-invalid={!!fieldErrors.description}
            aria-describedby={
              fieldErrors.description ? 'acc-desc-error' : undefined
            }
          />
          {fieldErrors.description && (
            <p
              id="acc-desc-error"
              className="mt-1 text-[13px] text-destructive"
            >
              {fieldErrors.description}
            </p>
          )}

          {/* Inline AI polish suggestion in form */}
          {formPolishResult && (
            <div className="mt-2 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                  AI suggestion
                </span>
              </div>
              <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                {formPolishResult}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!formPolishResult) return;
                    handleChange('description', formPolishResult);
                    setFormPolishResult(null);
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 disabled:opacity-50"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Use this
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormPolishing(true);
                    setFormPolishResult(null);
                    const params: {
                      id?: string;
                      description?: string;
                      impact?: string;
                      category?: string;
                    } = editingId
                      ? { id: editingId }
                      : {
                          description: formData.description,
                          impact: formData.impact || undefined,
                          category: formData.category || undefined,
                        };
                    polishAccomplishment.mutate(params, {
                      onSuccess: (result) => {
                        setFormPolishResult(
                          (result as { polished: string }).polished,
                        );
                        setFormPolishing(false);
                      },
                      onError: () => {
                        setFormPolishing(false);
                      },
                    });
                  }}
                  disabled={formPolishing}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai transition-colors hover:bg-ai-muted hover:text-ai disabled:opacity-50"
                >
                  <Sparkles className="h-3 w-3" />
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={() => setFormPolishResult(null)}
                  className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {polishAccomplishment.error && !formPolishing && (
            <p className="mt-1 text-[13px] text-destructive">
              {polishAccomplishment.error.message}
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="acc-impact"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Impact{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="acc-impact"
              type="text"
              value={formData.impact}
              onChange={(e) => handleChange('impact', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Reduced costs by $50K/year"
            />
          </div>

          <div>
            <label
              htmlFor="acc-category"
              className="mb-1.5 block text-[14px] font-medium text-foreground"
            >
              Category{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <select
              id="acc-category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">No category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <FieldHeader htmlFor="acc-tags" label="Tags" hint="(comma-separated, optional)">
            <button
              type="button"
              disabled={!formData.description.trim() || suggestTags.isPending}
              onClick={() => {
                const allExistingTags = Array.from(
                  new Set(
                    accomplishmentList
                      .flatMap((a) => a.tags ?? [])
                      .map((t) => t.trim())
                      .filter(Boolean),
                  ),
                );
                suggestTags.mutate(
                  {
                    description: formData.description,
                    impact: formData.impact || undefined,
                    category: formData.category || undefined,
                    existing_tags: allExistingTags.length
                      ? allExistingTags
                      : undefined,
                  },
                  {
                    onSuccess: (tags) => {
                      const existing = formData.tags
                        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
                        : [];
                      const merged = Array.from(
                        new Set([...existing, ...tags]),
                      );
                      handleChange('tags', merged.join(', '));
                    },
                  },
                );
              }}
              className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-[12px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
              title="Use AI to suggest tags based on the description ($0.01)"
            >
              {suggestTags.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              AI Tags
            </button>
          </FieldHeader>
          <input
            id="acc-tags"
            type="text"
            value={formData.tags}
            onChange={(e) => handleChange('tags', e.target.value)}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="devops, automation, ci-cd"
          />
          {suggestTags.error && (
            <p className="mt-1 text-[13px] text-destructive">
              {suggestTags.error.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <button
            type="submit"
            disabled={isPending || (!!editingId && !unsaved.hasChanges)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editingId ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="rounded-md px-4 py-2 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormCard>
  );

  if (accomplishments.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (accomplishments.error) {
    return (
      <div>
        <PageHeader title="Accomplishments" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load accomplishments. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Accomplishments"
        description="Your career accomplishment database. The more you add, the better your tailored CVs."
      >
        <button
          type="button"
          onClick={openSuggestPanel}
          className="inline-flex items-center gap-2 rounded-md bg-ai px-3 py-2 text-[14px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ai focus:ring-offset-2"
        >
          <Sparkles className="h-4 w-4" />
          AI Suggest
        </button>
        {!isFormOpen && (
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        )}
      </PageHeader>

      {/* AI Suggest panel */}
      {showSuggestPanel && (
        <div ref={suggestPanelRef} className="mt-4 rounded-lg border border-foreground bg-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ai" />
              <h2 className="text-[16px] font-semibold text-foreground">
                AI Accomplishment Suggestions
              </h2>
            </div>
            <button
              type="button"
              onClick={closeSuggestPanel}
              className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close suggestions"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {suggestions.length === 0 ? (
            <form onSubmit={handleGenerateSuggestions} className="mt-3">
              <p className="text-[14px] text-muted-foreground">
                Select which context to use for suggestions. Existing
                accomplishments are excluded automatically.
              </p>

              <div className="mt-4 space-y-2.5">
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={includeJobDescriptions}
                    onChange={(e) => setIncludeJobDescriptions(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-[14px] text-foreground">
                    Use job descriptions
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={includeRoleHighlights}
                    onChange={(e) => setIncludeRoleHighlights(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-[14px] text-foreground">
                    Use job role highlights
                  </span>
                </label>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="suggest-job-title"
                  className="mb-1.5 block text-[14px] font-medium text-foreground"
                >
                  Job Title{' '}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </label>
                <input
                  id="suggest-job-title"
                  type="text"
                  value={suggestJobTitle}
                  onChange={(e) => setSuggestJobTitle(e.target.value)}
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="e.g. Senior Software Engineer"
                  autoFocus
                />
              </div>

              {suggestAccomplishments.error && (
                <div
                  className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
                  role="alert"
                >
                  {suggestAccomplishments.error.message}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={
                    (!suggestJobTitle.trim() &&
                      !includeJobDescriptions &&
                      !includeRoleHighlights) ||
                    suggestAccomplishments.isPending
                  }
                  className="inline-flex items-center gap-2 rounded-md bg-ai px-4 py-2 text-[15px] font-medium text-ai-foreground transition-colors hover:bg-ai/85 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  {suggestAccomplishments.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {suggestAccomplishments.isPending
                    ? 'Generating...'
                    : 'Generate Suggestions'}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <p className="text-[14px] text-muted-foreground">
                  {suggestions.length} suggestion
                  {suggestions.length !== 1 ? 's' : ''}
                  {suggestJobTitle.trim() && (
                    <>
                      {' '}for{' '}
                      <span className="font-medium text-foreground">
                        {suggestJobTitle}
                      </span>
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSuggestions(
                      selectedSuggestions.size === suggestions.length
                        ? new Set()
                        : new Set(suggestions.map((_, i) => i)),
                    );
                  }}
                  className="text-[13px] font-medium text-primary transition-colors hover:text-primary/80"
                >
                  {selectedSuggestions.size === suggestions.length
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
              </div>

              <div className="mt-3 max-h-[50vh] space-y-1.5 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <label
                    key={index}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2.5 transition-colors hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSuggestions.has(index)}
                      onChange={() => toggleSuggestion(index)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] leading-relaxed text-foreground">
                        {suggestion.description}
                      </p>
                      {suggestion.impact && (
                        <p className="mt-1 text-[13px] text-muted-foreground">
                          <span className="font-medium">Impact:</span>{' '}
                          {suggestion.impact}
                        </p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {suggestion.category && (
                          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {suggestion.category}
                          </span>
                        )}
                        {suggestion.suggested_tags?.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                          >
                            <Tag className="h-2.5 w-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {bulkCreateAccomplishments.error && (
                <div
                  className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
                  role="alert"
                >
                  {bulkCreateAccomplishments.error.message}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                <button
                  type="button"
                  onClick={closeSuggestPanel}
                  className="rounded-md px-3 py-2 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={handleAcceptSuggestions}
                  disabled={
                    selectedSuggestions.size === 0 ||
                    bulkCreateAccomplishments.isPending
                  }
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                >
                  {bulkCreateAccomplishments.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add Selected ({selectedSuggestions.size})
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search accomplishments..."
            className="block w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk action bar */}
      {accomplishmentList.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={bulk.allSelected}
              ref={(el) => {
                if (el) el.indeterminate = bulk.someSelected;
              }}
              onChange={bulk.toggleAll}
              className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
            />
            {inSelectionMode
              ? `${bulk.count} of ${accomplishmentList.length}`
              : 'Select all'}
          </label>
          <BulkActionBar
            selectedCount={bulk.count}
            selectedIds={bulk.selectedArray}
            actions={bulkActions}
            onDeselectAll={bulk.deselectAll}
          />
        </div>
      )}

      {/* Create form — the edit form renders inline in place of the entry being edited */}
      {isFormOpen && !editingId && accomplishmentForm}

      {/* Accomplishment list */}
      {accomplishmentList.length === 0 && !isFormOpen ? (
        <div className="mt-6 rounded-lg border border-dashed border-border p-12 text-center">
          <Star className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-[15px] text-muted-foreground">
            {searchTerm || categoryFilter
              ? 'No accomplishments match your filters.'
              : 'No accomplishments yet. Start building your database.'}
          </p>
          {!searchTerm && !categoryFilter && (
            <div className="mt-3 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={openCreateForm}
                className="inline-flex items-center gap-1.5 text-[14px] font-medium text-primary transition-colors hover:text-primary/80"
              >
                <Plus className="h-3.5 w-3.5" />
                Add manually
              </button>
              <span className="text-[13px] text-muted-foreground">or</span>
              <button
                type="button"
                onClick={openSuggestPanel}
                className="inline-flex items-center gap-1.5 rounded-md bg-ai px-3 py-1.5 text-[14px] font-medium text-ai-foreground shadow-sm transition-colors hover:bg-ai/85"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Get AI suggestions
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {accomplishmentList.map((accomplishment) =>
            editingId === accomplishment.id ? (
              <div key={accomplishment.id} ref={editFormRef}>{accomplishmentForm}</div>
            ) : (
            <EntryCard
              key={accomplishment.id}
              selected={bulk.isSelected(accomplishment.id)}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => handlePolish(accomplishment.id)}
                    disabled={polishedTexts[accomplishment.id] === null}
                    className="rounded-md border border-ai-border/40 bg-ai-muted p-2.5 sm:p-1.5 text-ai transition-colors hover:bg-ai hover:text-ai-foreground focus:outline-none focus:ring-2 focus:ring-ai disabled:opacity-50"
                    aria-label="AI polish"
                    title="AI polish"
                  >
                    {polishedTexts[accomplishment.id] === null ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditForm(accomplishment)}
                    className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Edit accomplishment"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <InlineDeleteConfirm
                    onConfirm={() => handleDelete(accomplishment.id)}
                    isDeleting={deletingId === accomplishment.id}
                    ariaLabel="Delete accomplishment"
                  />
                </>
              }
            >
              <div className="flex items-start gap-2">
                <SelectionCheckbox
                  checked={bulk.isSelected(accomplishment.id)}
                  onChange={() => bulk.toggle(accomplishment.id)}
                  visible={inSelectionMode}
                  ariaLabel="Select accomplishment"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] leading-relaxed text-foreground">
                    {accomplishment.description}
                  </p>

                  {accomplishment.impact && (
                    <p className="mt-1 text-[14px] text-muted-foreground">
                      <span className="font-medium">Impact:</span>{' '}
                      {accomplishment.impact}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {accomplishment.category && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[12px] font-medium text-muted-foreground">
                        {accomplishment.category}
                      </span>
                    )}
                    {accomplishment.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[12px] text-muted-foreground"
                      >
                        <Tag className="h-2.5 w-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Inline AI polish suggestion */}
                  {accomplishment.id in polishedTexts && polishedTexts[accomplishment.id] !== undefined && (
                    polishedTexts[accomplishment.id] === null ? (
                      <div className="mt-2 flex items-center gap-1.5 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <Loader2 className="h-3 w-3 animate-spin text-ai" />
                        <span className="text-[13px] text-muted-foreground">
                          Polishing...
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 rounded-md border border-ai-border/30 bg-ai-muted px-2.5 py-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 shrink-0 text-ai" />
                          <span className="text-[12px] font-medium uppercase tracking-wide text-ai">
                            AI suggestion
                          </span>
                        </div>
                        <p className="mt-1 text-[14px] leading-relaxed text-foreground">
                          {polishedTexts[accomplishment.id]}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleAcceptPolish(accomplishment.id)}
                            disabled={updateAccomplishment.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-ai px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai-foreground transition-colors hover:bg-ai/90 disabled:opacity-50"
                          >
                            {updateAccomplishment.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin text-ai" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3" />
                            )}
                            Use this
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePolish(accomplishment.id)}
                            disabled={polishedTexts[accomplishment.id] === null}
                            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-ai transition-colors hover:bg-ai-muted hover:text-ai disabled:opacity-50"
                          >
                            <Sparkles className="h-3 w-3" />
                            Regenerate
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissPolish(accomplishment.id)}
                            className="rounded-md px-3 py-1.5 text-[13px] min-h-[44px] sm:min-h-0 sm:px-2 sm:py-1 sm:text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )
                  )}

                  {/* Inline AI polish error */}
                  {polishErrors[accomplishment.id] && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2">
                      <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                      <span className="text-[13px] text-destructive">
                        {polishErrors[accomplishment.id]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </EntryCard>
            ),
          )}
        </div>
      )}

      {deleteAccomplishment.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {deleteAccomplishment.error.message}
        </div>
      )}

      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}
